import 'swiper/css';
import 'swiper/css/pagination';
import '../../components/profile/styles/SwiperCustomPagination.css';

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { apiEvents } from '../../api/apiEvents.api';
import { apiUser } from '../../api/apiUser.api';
import { optimisticToggleJoin } from '../../utils/eventJoin';
import { JoinsSheet } from './JoinsSheet';
import { EventPhoto } from './EventPhoto';
import { formatEventDateFull } from './eventDate';
import { useSubscription } from '../../hooks/useSubscription';
import config from '../../config';

// Порог раскрытия длинного описания
const DESCRIPTION_LIMIT = 220;

// Свайп фото — точно как в карточке анкеты: сегменты-прогресс сверху, затемнение снизу.
// Занимает ~40% экрана и слит с карточкой информации в один блок.
function PhotoSwiper({ photos, alt }) {
  const photosList = photos?.length ? photos : [null];

  return (
    <div className="relative w-full">
      <div className="w-full relative">
        <div
          className="custom-pagination"
          style={{
            position: 'absolute',
            top: 0,
            zIndex: 10,
            left: '50%',
            transform: 'translateX(-50%)'
          }}
        ></div>
      </div>
      <div className="relative overflow-hidden">
        <Swiper
          modules={[Pagination]}
          spaceBetween={0}
          slidesPerView={1}
          className="w-full h-[40vh] min-h-[300px]"
          pagination={{ el: '.custom-pagination' }}
        >
          {photosList.map((photo, i) => (
            <SwiperSlide key={i}>
              <EventPhoto
                src={photo}
                alt={alt}
                className="z-[1] w-full h-[40vh] min-h-[300px] object-cover select-none"
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className="absolute left-0 right-0 bottom-0 w-full h-[130px] z-[5] pointer-events-none"
          style={{ background: 'linear-gradient(to top, #010D0D 0%, transparent 100%)' }}
        />
      </div>
    </div>
  );
}

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const { isActiveSubscription } = useSubscription();
  const subscribed = isActiveSubscription === true;

  const [event, setEvent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [joinsOpen, setJoinsOpen] = useState(false);
  const [ownAvatar, setOwnAvatar] = useState(null);

  // Свой аватар — для оптимистичного отображения в участниках
  useEffect(() => {
    apiUser
      .fetchMe(axiosPrivate)
      .then((data) => setOwnAvatar(data?.photos?.[0] || null))
      .catch(() => {});
  }, [axiosPrivate]);

  useEffect(() => {
    let cancelled = false;

    apiEvents
      .fetchEvent(axiosPrivate, id)
      .then((data) => {
        if (!cancelled) setEvent(data);
      })
      .catch((error) => {
        console.error('Error fetching event:', error);
        toast.error(error?.response?.data?.message || 'Мероприятие не найдено');
        navigate(-1);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [axiosPrivate, id, navigate]);

  const handleJoin = async () => {
    if (isJoining || !event) return;
    const prevEvent = event;
    const willJoin = !event.isJoined;
    setIsJoining(true);

    // Оптимистично: статус, счётчик и аватары меняются мгновенно
    setEvent(optimisticToggleJoin(event, willJoin, ownAvatar));

    try {
      await apiEvents.toggleJoin(axiosPrivate, id);
      toast.success(willJoin ? 'Вы отметились: «Хочу пойти»!' : 'Участие отменено');

      // Тихо подтягиваем актуальные данные участников с сервера
      apiEvents
        .fetchEvent(axiosPrivate, id)
        .then((data) => setEvent(data))
        .catch(() => {});
    } catch (error) {
      setEvent(prevEvent);
      toast.error(error?.response?.data?.message || 'Не удалось сохранить ответ');
    } finally {
      setIsJoining(false);
    }
  };

  if (isLoading || !event) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <DotSpinner />
      </div>
    );
  }

  const dateLabel = formatEventDateFull(event.date);

  const avatars = event.joinUsers || [];
  const extraCount = Math.max(0, (event.joins || 0) - avatars.length);

  const description = event.description || '';
  const isLong = description.length > DESCRIPTION_LIMIT;
  const shortDescription = isLong && !expanded ? `${description.slice(0, DESCRIPTION_LIMIT).trimEnd()}…` : description;

  return (
    <div className="w-full h-full overflow-y-auto pb-[170px]">
      {/* Фото и информация — единый блок, как карточка анкеты */}
      <div className="mx-4 mt-[8px] bg-[#010D0D] rounded-[16px] overflow-hidden">
        <PhotoSwiper photos={event.photosUrls?.length ? event.photosUrls : [event.coverUrl]} alt={event.title} />

        <div className="p-4 flex flex-col gap-4 ">
          <h1 className="text-white text-[22px] font-bold font-raleway leading-snug break-words">{event.title}</h1>

          {avatars.length > 0 && (
            <button
              onClick={() => setJoinsOpen(true)}
              className="w-full flex items-center gap-2 border-b-[1px] border-white/30 pb-3"
            >
              <div
                className="flex items-center"
                onClick={(e) => {
                  if (!subscribed) {
                    e.stopPropagation();
                    navigate(config.ROUTES.PAYWALL);
                  }
                }}
              >
                {avatars.slice(0, 3).map((photo, i) => (
                  <img
                    key={i}
                    src={photo}
                    alt=""
                    className={`w-[48px] h-[48px] rounded-full object-cover -ml-1.5 first:ml-0 ${
                      subscribed || i === (event.myJoinIndex ?? -1) ? '' : 'blur-[5px] select-none'
                    }`}
                  />
                ))}
                {extraCount > 0 && <span className="ml-2 text-[18px] font-bold text-white">+{extraCount}</span>}
              </div>
              <span className="ml-auto text-[#A1F69E] text-[16px] font-bold">Узнать кто идёт</span>
            </button>
          )}

          {/* О мероприятии */}
          <div>
            <h2 className="text-white text-[18px] font-bold font-raleway mb-1.5">О мероприятии</h2>
            <p className="text-white text-[16px] whitespace-pre-line leading-relaxed text-justify break-all">
              {shortDescription}
            </p>
            {isLong && (
              <button onClick={() => setExpanded((v) => !v)} className="text-[#A1F69E] text-base font-normal mt-1">
                {expanded ? 'Свернуть описание' : 'Читать полностью'}
              </button>
            )}
          </div>

          {/* Дата, место, сайт */}
          <div className="flex flex-col gap-2.5">
            <div className="flex flex-col">
              <h2 className="text-white text-[18px] font-bold font-raleway mb-1.5">Дата и время</h2>
              <p className="flex items-center text-white text-base">
                {dateLabel}
              </p>
            </div>

            <div className="flex flex-col">
              <h2 className="text-white text-[18px] font-bold font-raleway mb-1.5">Место</h2>
              <div className="flex items-center gap-2">
                <p className="flex items-center text-white text-base min-w-0 flex-1 text-justify">
                  {event.city}, {event.place}
                </p>
                <button
                  type="button"
                  aria-label="Скопировать адрес"
                  onClick={() => {
                    const address = `${event.city}, ${event.place}`;
                    if (navigator.clipboard?.writeText) {
                      navigator.clipboard.writeText(address);
                    }
                    toast.success('Адрес скопирован');
                  }}
                  className="shrink-0 p-1.5 active:opacity-70"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z"
                      fill="#A1F69E"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {event.website && (
              <a href={event.website} target="_blank" rel="noreferrer" className="flex flex-col">
                <h2 className="text-white text-[18px] font-bold font-raleway mb-1.5">Сайт</h2>
                <p className="text-[#A1F69E] text-base truncate underline">
                  {event.website.replace(/^https?:\/\//, '')}
                </p>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Fixed-кнопки внизу — как в остальных блоках, отступы 16px */}
      <div className="fixed bottom-[28px] left-4 right-4 flex gap-2 z-10">
        <button
          onClick={handleJoin}
          disabled={isJoining}
          className={`flex-1 h-[56px] rounded-[400px] font-raleway font-medium text-[18px] transition-colors ${
            event.isJoined
              ? 'bg-[#010D0D]/60 backdrop-blur-md border border-[#A1F69E] text-[#A1F69E] active:bg-[#043939]'
              : 'bg-[#A1F69E] text-[#010D0D] active:bg-[#7DD279]'
          }`}
        >
          {event.isJoined ? 'Я иду' : 'Хочу пойти'}
        </button>
        <button
          onClick={() => navigate(config.ROUTES.EVENTS.INVITE(event._id))}
          className="flex-1 h-[56px] rounded-[400px] font-raleway font-medium text-[18px] bg-[#010D0D]/60 backdrop-blur-md text-white active:bg-white/20"
        >
          Пригласить
        </button>
      </div>

      <JoinsSheet event={event} isOpen={joinsOpen} onClose={() => setJoinsOpen(false)} />
    </div>
  );
}

export default EventDetail;
